import React, { useState, useEffect } from 'react'
import {
  Box,
  Flex,
  PrimaryButton,
  Text,
  Image,
  Heading,
  IconButton,
  LoadingIndicator,
  SkeletonBox,
} from '@moonpig/launchpad-components'
import { breakpointUp, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { spacingPx, theme } from '@moonpig/launchpad-theme'
import { IconSystemBin, IconSystemChevronUp } from '@moonpig/launchpad-assets'
import {
  Status,
  useFetchMultiBuyList,
  useRemoveFromMultiBuyList,
  MULTI_BUY_LIST_REMOVE_ITEM_EVENT_NAME,
  MultiBuyListRemoveItemEvent,
} from '@moonpig/web-shared-products'
import { useFindLocaleText } from '../../text-localisation'
import { useStoreId } from '@moonpig/web-core-stores'
import {
  createGenericImpressionEvent,
  createGenericSelectContentEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import { EmptyState } from './components/EmptyState'

const PANEL_MAX_WIDTH = '600px'
const PANEL_CORNER_RADIUS = '16px'
const ITEM_WIDTH = { xs: '56px', md: '70px' }
const ITEM_HEIGHT = { xs: '80px', md: '100px' }

const StyledCounter = styled.div`
  border-radius: 50%;
  ${s({
    bgcolor: 'colorBackgroundInformation',
    width: 24,
    height: 24,
    color: 'colorInteractionSelectedState',
    typography: 'typeBodyText',
    ml: 4,
  })}
`

const StyledFixedWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 999;
  pointer-events: none;
  padding-right: var(--removed-body-scroll-bar-size, 0px);
`

const StyledPanel = styled(Box)<{ $isExpanded: boolean }>`
  pointer-events: auto;
  cursor: default;  
  ${({ $isExpanded }) =>
    s({
      bgcolor: 'colorBackground01',
      boxShadow: theme.shadows.boxShadow02,
      maxWidth: { md: PANEL_MAX_WIDTH },
      marginLeft: 'auto',
      px: 6,
      pb: $isExpanded ? 6 : 0,
    })}
  
  ${breakpointUp('md')} {
    margin-right: 24px;
  }

  border-top-left-radius: ${PANEL_CORNER_RADIUS};
  border-top-right-radius: ${PANEL_CORNER_RADIUS};
`

const StyledHeader = styled.button`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    ${s({ py: 5 })}
`

const StyledChevron = styled(IconSystemChevronUp)`
    flex-shrink: 0;
    transition: transform 200ms ease-in-out;
    ${s({
      color: 'colorInteractionIcon',
      width: 28,
      height: 28,
    })};

    &.collapsed {
        transform: rotate(180deg);
    }
`

const StyledItems = styled(Flex)`
  align-items: center;
  overflow-x: auto;
  gap: ${spacingPx(5)};
  ${s({
    padding: 6,
    marginBottom: 6,
    borderColor: 'colorBorder03',
    border: '2px dashed',
  })}
`

const StyledItemImage = styled(Image)`
  ${s({
    boxShadow: theme.shadows.boxShadow01,
    width: '100%',
    height: '100%',
  })}
`

const StyledContentWidth = styled.div`
  pointer-events: none;
  ${({ theme: { siteWidth } }) => s({ maxWidth: siteWidth, mx: 'auto' })}
`

const StyledItemWrapper = styled(Box)`
  position: relative;
  flex: 0 0 auto;
`
const StyledDeleteButton = styled(IconButton)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
  z-index: 1;
  ${s({
    width: 28,
    height: 28,
    minHeight: 28,
    minWidth: 28,
    bottom: -6,
    bgcolor: 'colorBackground01',
    color: 'colorFeedbackError',
    boxShadow: theme.shadows.boxShadow01,
  })}

  svg {
    ${s({
      width: 16,
      height: 16,
    })}
  }
`

const handlePersonalise = (
  region: string,
  productKey: string,
  variantKey: string,
  productVariantSku: string,
) => {
  trackGAEvent(
    createGenericSelectContentEvent({
      eventCategory: 'multibuy list',
      eventAction: 'personalise',
      eventLabel: 'multibuy list personalise button',
    }),
  )
  const url = `/${region}/customise/product/${productKey}/${variantKey}/?productVariantSku=${productVariantSku}`
  window.location.assign(url)
}

export const ShoppingList = ({
  context,
}: {
  context: 'gallery' | 'search results'
}) => {
  const localiseText = useFindLocaleText()
  const [isExpanded, setIsExpanded] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isMissingFields, setIsMissingFields] = useState(false)
  const [itemsToDelete, setItemsToDelete] = useState(new Set<string>())
  const region = useStoreId()

  const { fetchMultiBuyListLoading, multiBuyListItems, multiBuyListItemCount } =
    useFetchMultiBuyList()

  const { removeFromMultiBuyList } = useRemoveFromMultiBuyList()

  const productIdForFirstItemInList = multiBuyListItems[0]?.variant.productId

  const variantKeyForFirstItemInList = multiBuyListItems[0]?.variant.key

  const productVariantSkuForFirstItemInList =
    multiBuyListItems[0]?.variant.sku ?? ''

  useEffect(() => {
    trackGAEvent(
      createGenericImpressionEvent({
        category: 'multibuy list',
        action: 'view multibuy list',
        label: `multibuy list impression - ${context}`,
      }),
    )
  }, [context])

  const DeleteLoadingIcon = () => (
    <LoadingIndicator label={'Removing'} size={8} color="colorFeedbackError" />
  )

  const handleOnRemove = async (id: string) => {
    const productId = multiBuyListItems.find(item => item.id === id)?.variant
      .productId
    setErrorMessage(null)
    setItemsToDelete(prev => new Set(prev).add(id))

    const response = await removeFromMultiBuyList({ multiBuyListItemId: id })

    setItemsToDelete(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })

    if (response.error?.errorMessage) {
      setErrorMessage(response.error.errorMessage)
    }

    if (!response.error) {
      if (productId && response.productMultiBuyListCount !== undefined) {
        window.dispatchEvent(
          new CustomEvent<MultiBuyListRemoveItemEvent>(
            MULTI_BUY_LIST_REMOVE_ITEM_EVENT_NAME,
            {
              detail: {
                productId: productId,
              },
            },
          ),
        )
      }

      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: 'multibuy list',
          eventAction: 'removed',
          eventLabel: 'multibuy list remove button',
        }),
      )
    }
  }

  const toggleExpanded = (eventLabel: string) => {
    setIsExpanded(prev => !prev)
    trackGAEvent(
      createGenericSelectContentEvent({
        eventCategory: 'multibuy list',
        eventAction: !isExpanded === true ? 'expanded' : 'collapsed',
        eventLabel,
      }),
    )
  }
  const reversedMultiBuyItems = [...multiBuyListItems].reverse()

  const hasListItems = multiBuyListItemCount > 0
  const isListEmpty = !fetchMultiBuyListLoading && !hasListItems

  return (
    <>
      <Status
        error={{
          status: !!errorMessage || isMissingFields,
          message:
            errorMessage ??
            (isMissingFields
              ? localiseText('find.something_went_wrong')
              : undefined),
          onDismiss: () => {
            setErrorMessage(null)
            setIsMissingFields(false)
          },
        }}
      />
      <StyledFixedWrapper>
        <StyledContentWidth>
          <StyledPanel
            role="region"
            aria-label={localiseText('find.shopping_list.title')}
            data-testid="shopping-list"
            $isExpanded={isExpanded}
          >
            <StyledHeader
              type="button"
              data-testid="shopping-list-header"
              onClick={() =>
                toggleExpanded('multibuy list header toggle chevron')
              }
              aria-expanded={isExpanded}
              aria-label={
                isExpanded
                  ? localiseText('find.shopping_list_collapse')
                  : localiseText('find.shopping_list_expand')
              }
            >
              <Flex alignItems="center">
                <Heading
                  level="h3"
                  typography="typeDisplay05"
                  color="colorTextHeadline"
                  margin="0"
                >
                  {localiseText('find.shopping_list.title')}
                </Heading>
                {hasListItems && (
                  <StyledCounter data-testid="counter">
                    <Text fontWeight="bold">{multiBuyListItemCount}</Text>
                  </StyledCounter>
                )}
              </Flex>
              <StyledChevron
                className={isExpanded ? '' : 'collapsed'}
                aria-hidden
              />
            </StyledHeader>
            {isExpanded && (
              <>
                <StyledItems>
                  {fetchMultiBuyListLoading ? (
                    <SkeletonBox
                      width={ITEM_WIDTH}
                      minWidth={ITEM_WIDTH}
                      height={ITEM_HEIGHT}
                      data-testid="shopping-list-item-skeleton"
                    />
                  ) : (
                    reversedMultiBuyItems.map((item, index) => (
                      <StyledItemWrapper
                        width={ITEM_WIDTH}
                        minWidth={ITEM_WIDTH}
                        key={item.id}
                      >
                        <StyledItemImage
                          src={
                            item.variant.productImages.mainImage.small.jpgUrl
                          }
                          alt={item.variant.sku || ''}
                        />
                        <StyledDeleteButton
                          icon={
                            itemsToDelete.has(item.id)
                              ? DeleteLoadingIcon
                              : IconSystemBin
                          }
                          disabled={itemsToDelete.has(item.id)}
                          label={localiseText(
                            'find.shopping_list_remove_item',
                            {
                              cardPosition: index + 1,
                            },
                          )}
                          onClick={e => {
                            e.stopPropagation()
                            handleOnRemove(item.id)
                          }}
                        />
                      </StyledItemWrapper>
                    ))
                  )}
                  {isListEmpty && <EmptyState />}
                </StyledItems>
                {!isListEmpty && (
                  <PrimaryButton
                    width="100%"
                    loading={fetchMultiBuyListLoading}
                    onClick={() => {
                      if (
                        !productIdForFirstItemInList ||
                        !variantKeyForFirstItemInList
                      ) {
                        setIsMissingFields(true)
                        return
                      }

                      handlePersonalise(
                        region,
                        productIdForFirstItemInList,
                        variantKeyForFirstItemInList,
                        productVariantSkuForFirstItemInList,
                      )
                    }}
                  >
                    {!fetchMultiBuyListLoading &&
                      localiseText('find.shopping_list.cta.personalise')}
                  </PrimaryButton>
                )}
                {isListEmpty && (
                  <PrimaryButton
                    width="100%"
                    onClick={() =>
                      toggleExpanded('multibuy list continue browsing button')
                    }
                  >
                    {localiseText('find.shopping_list.cta.continue_browsing')}
                  </PrimaryButton>
                )}
              </>
            )}
          </StyledPanel>
        </StyledContentWidth>
      </StyledFixedWrapper>
    </>
  )
}
